import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { switchMap, take } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { Tab } from '../../tabs-v2/interfaces/tab.interface';
import { NotificationTab } from '../../tabs-v2/enums/tabs.enum';
import { PaymentsTab } from '../../tabs/enums/tabs.enum';
import { NotificationService } from '../../../services/notifications.service';
import { BaseService } from '../../../services/base.service';
import { TaskService } from '../../../services/task.service';
import { FiscalNotificationService } from '../../../services/fiscal-notification.service';

@Component({
  selector: 'app-notification-menu',
  standalone: false,
  templateUrl: './notification-menu.component.html',
  styleUrls: ['./notification-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class NotificationMenuComponent implements OnInit {
  protected selectedTab!: Tab<NotificationTab>;
  public tabs: Tab<NotificationTab>[] = [
    { name: 'inbox', value: NotificationTab.INBOX, default: true },
    { name: 'archived', value: NotificationTab.ARCHIVED },
  ];
  public tabsAlive = true; // для “ремонту” вкладок (щоб індикатор перескочив)

  public primaryColor = '';
  public textColor = '';

  constructor(
    protected readonly notificationService: NotificationService,
    protected readonly baseService: BaseService,
    private readonly taskService: TaskService,
    protected readonly fiscalNotifications: FiscalNotificationService,
  ) {}

  ngOnInit(): void {
    this.primaryColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-color')
      .trim()
      .replace('#', '');
    this.textColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--text-color')
      .trim()
      .replace('#', '');
  }

  protected onChangeTab(tab: Tab<NotificationTab>): void {
    this.selectedTab = tab; // меню не закриваємо
  }

  private activateArchivedTab(): void {
    const archived = this.tabs.find((t) => t.value === NotificationTab.ARCHIVED);
    if (!archived) return;

    // 1) внутрішній selected
    this.selectedTab = archived;

    // 2) якщо app-tabs-v2 орієнтується на "default" при ініціалізації — підсвічуємо Архів
    this.tabs = this.tabs.map((t) => ({ ...t, default: t.value === NotificationTab.ARCHIVED }));

    // 3) універсально пересадимо компонент вкладок на 1 кадр, щоб індикатор точно змістився
    this.tabsAlive = false;
    setTimeout(() => (this.tabsAlive = true));

    // 4) прокрутимо список нагору після перемикання
    setTimeout(() => {
      const list = document.querySelector('.notification-menu .np-list');
      list?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  protected onMarkAllRead(evt: MouseEvent): void {
    evt.stopPropagation(); // не закривати меню

    // Оптимістично: все з inbox -> у архів, inbox стає порожнім
    const unread = this.notificationService.unreadNotificationList ?? [];
    if (unread.length) {
      const moved = unread.map((n) => ({ ...n, is_read: true }));
      this.notificationService.readNotificationList = [
        ...moved,
        ...(this.notificationService.readNotificationList ?? []),
      ];
      this.notificationService.unreadNotificationList = [];
    }

    // Активуємо вкладку "Архів" (оранжева відмітка переїде туди)
    this.selectedTab = this.tabs.find((t) => t.value === NotificationTab.INBOX)!;

    // Синхронізація з беком + оновлення бейджа
    this.notificationService
      .readAllNotification()
      .pipe(
        switchMap(() =>
          forkJoin({
            unread: this.notificationService.getAllNotification({ is_read: false }),
            read: this.notificationService.getAllNotification({ is_read: true }),
            _cnt: this.notificationService.getCountNotification(),
          }),
        ),
        take(1),
      )
      .subscribe(({ unread, read }) => {
        this.notificationService.unreadNotificationList = unread ?? [];
        this.notificationService.readNotificationList = read ?? [];
      });
  }
  activeId: any = null;
  toggleActive(n: any, evt: MouseEvent): void {
    evt.stopPropagation();
    const id = n?.id ?? n?.created_at;
    this.activeId = this.activeId === id ? null : id;
  }
  moveToArchive(n: any, evt?: MouseEvent): void {
    evt?.stopPropagation(); // не закривати меню
    if (!n || n.is_read) return;

    const id = n.id ?? n.created_at;

    // оптимістично: прибрати з inbox і додати в архів
    this.notificationService.unreadNotificationList = (this.notificationService.unreadNotificationList ?? []).filter(
      (x) => (x.id ?? x.created_at) !== id,
    );

    this.notificationService.readNotificationList = [
      { ...n, is_read: true },
      ...(this.notificationService.readNotificationList ?? []),
    ];

    // бекенд + оновлення бейджа
    this.notificationService
      .readNotification(n.id)
      .pipe(
        switchMap(() => this.notificationService.getCountNotification()),
        take(1),
      )
      .subscribe({
        // за потреби можна добрати свіжі списки з бекенда
        error: () => {
          /* опційно показати тост і зробити відкат */
        },
      });
  }

  /** A notification about a task carries its id: it opens the task, also when it is read already */
  private openTask(n: any): void {
    const taskId: unknown = n?.x_data?.task_id;
    if (typeof taskId !== 'string' || !taskId) return;

    this.taskService.openTaskDetail(taskId).subscribe();
  }

  onItemClick(n: any, evt: MouseEvent): void {
    evt.stopPropagation();
    this.openTask(n);
    if (this.selectedTab?.value !== NotificationTab.INBOX) return;
    if (!n || n.is_read) return;

    const id = n.id ?? n.created_at;

    this.notificationService.unreadNotificationList = (this.notificationService.unreadNotificationList ?? []).filter(
      (x) => (x.id ?? x.created_at) !== id,
    );

    this.notificationService.readNotificationList = [
      { ...n, is_read: true },
      ...(this.notificationService.readNotificationList ?? []),
    ];

    this.notificationService
      .readNotification(n.id)
      .pipe(
        switchMap(() => this.notificationService.getCountNotification()),
        take(1),
      )
      .subscribe();
  }

  getNotificationMessage(message?: string): string {
    if (!message) return '';

    const decoded = this.decodeHtml(message);
    if (this.containsHtmlAnchor(decoded)) return decoded;
    return this.linkifyText(decoded);
  }

  private containsHtmlAnchor(text: string): boolean {
    return /<a\s/i.test(text);
  }

  private linkifyText(text: string): string {
    return text.replace(/(https?:\/\/[^\s]+)/g, (url) => `<a href="${url}" target="_blank" rel="noopener">${url}</a>`);
  }

  private decodeHtml(text: string): string {
    if (!text.includes('&')) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  protected readonly PaymentsTab = PaymentsTab;
  protected readonly NotificationTab = NotificationTab;
}
